import React, { useEffect, useState } from 'react';
import { Table, Checkbox } from '../ui';
import { HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY } from '../../consts/consts';
import { NoContentTableComponent } from '../ui/NoContent';
import {
  ImportTableWrapper,
  ImportTableCheckboxesWrapper,
  ImportTableCheckboxesList,
  ImportTableSectionWrapper
} from '../ui/Layout/ImportLayout';
import { isTabletOrSmaller } from '../../helpers/utility';

/**
 * This component is the second step of the import process.
 * It renders the import table contining the processed data from the import file
 * plus a status column that marks if a row is valid or not.
 * @param {Array} importTableColumns
 * @param {Array} importTableData
 * @param {number} customTableBodyHeight
 * @param {string} className
 */
export const ImportTableSection = ({ importTableColumns, importTableData, customTableBodyHeight, className }) => {
  const [tableBodyHeight, setTableBodyHeight] = useState(
    customTableBodyHeight || window.innerHeight - HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY
  );

  const [activeColumns, setActiveColumns] = useState([]);
  const [importColumnsCheckboxes, setImportColumnsCheckboxes] = useState([]);

  useEffect(() => {
    if (customTableBodyHeight) {
      setTableBodyHeight(customTableBodyHeight);
      return;
    }

    const handleResize = () => {
      setTableBodyHeight(window.innerHeight - HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [customTableBodyHeight]);

  // In the following useEffects a logic is implemented to hide/show columns in the table, based on checkboxes
  useEffect(() => {
    // The invalid columns checkboxes are unchecked by default
    const columnsCheckboxes = importTableColumns
      .filter((column) => !['row', 'status', 'error_messages'].includes(column.dataIndex))
      .map((column) => ({
        key: column.dataIndex,
        label: column.title,
        checked: column.className !== 'invalid-column',
        className: column.className === 'invalid-column' ? 'checkbox-invalid-column' : ''
      }));
    setImportColumnsCheckboxes(columnsCheckboxes);
  }, [importTableColumns]);

  // Based on the checkboxes, the columns are hidden or shown
  useEffect(() => {
    const activeColumns = importTableColumns.map((column) => {
      const isMetaColumn = ['row', 'status', 'error_messages'].includes(column.dataIndex);
      if (isMetaColumn) {
        return { ...column, hidden: false };
      }
      const columnCheckbox = importColumnsCheckboxes.find((checkbox) => checkbox.key === column.dataIndex);
      const isColumnHidden = !columnCheckbox?.checked;
      return { ...column, hidden: isColumnHidden };
    });
    setActiveColumns(activeColumns);
  }, [importColumnsCheckboxes]);

  const handleCheckboxClick = (key) => {
    const updatedCheckboxes = importColumnsCheckboxes.map((checkbox) => {
      if (checkbox.key === key) {
        return { ...checkbox, checked: !checkbox.checked };
      }
      return checkbox;
    });
    setImportColumnsCheckboxes(updatedCheckboxes);
  };

  const renderColumnCheckboxes = () => {
    return importColumnsCheckboxes.map((checkbox) => {
      return (
        <Checkbox
          key={checkbox.key}
          className={checkbox.className}
          checked={checkbox.checked}
          onChange={() => handleCheckboxClick(checkbox.key)}
        >
          {checkbox.label}
        </Checkbox>
      );
    });
  };

  const getRowClassName = (record) => {
    return record.valid_row ? 'valid-row' : 'invalid-row';
  };

  return (
    <ImportTableSectionWrapper className={className}>
      {importColumnsCheckboxes.length > 0 && (
        <ImportTableCheckboxesWrapper>
          <ImportTableCheckboxesList>{renderColumnCheckboxes()}</ImportTableCheckboxesList>
        </ImportTableCheckboxesWrapper>
      )}
      <ImportTableWrapper>
        <Table
          virtual={!isTabletOrSmaller} // Virtual list is disabled on mobile devices
          scroll={{ y: tableBodyHeight }}
          rowHoverable={false}
          pagination={false}
          columns={activeColumns}
          dataSource={importTableData}
          rowClassName={getRowClassName}
          locale={{
            emptyText: <NoContentTableComponent message="Keine Daten vorhanden." topic="Daten" />
          }}
        />
      </ImportTableWrapper>
    </ImportTableSectionWrapper>
  );
};
